import { HttpError } from '../app-kit/request-service';
import { isAbortedRequest } from './aborted-request';
import { autoBugReport } from './console-capture';
import { isCancelledByUnload, pageUnload, type UnloadWatch } from './page-unload';

export interface ReportNonHttpErrorDeps {
  report?: (source: 'api_error', message: string, extra?: Record<string, unknown>) => void;
  unload?: UnloadWatch;
}

/**
 * The query and mutation caches' error hook (`main.tsx`): file a bug report
 * for an error that is NOT an http failure.
 *
 * ⚠️ `apiJson` reports every http failure itself, so an `HttpError` arriving
 * here has already been filed — reporting it again would file the same failure
 * twice, under a different dedupe key (`query foo: …` vs `500 /api/x: …`), so
 * the 30 s window would not collapse them.
 *
 * But a `queryFn` can also throw something that never reached the network: a
 * parse of the response body, a mapping over a field the server stopped
 * sending, a thrown assertion. Nothing downstream of `apiJson` sees those, and
 * they are exactly the shape that renders a blank screen with no error. So
 * this hook keeps them — and only them.
 */
export function reportNonHttpError(err: unknown, context: string, deps: ReportNonHttpErrorDeps = {}): void {
  const report = deps.report ?? autoBugReport;
  const unload = deps.unload ?? pageUnload;
  if (err instanceof HttpError) return;   // already reported by apiJson
  if (isCancelledByUnload(err, unload)) return;   // a reload cancelled the fetch
  if (isAbortedRequest(err)) return;   // the app cancelled the fetch (aborted-request.ts)
  const message = err instanceof Error ? err.message : String(err);
  report('api_error', `${context}: ${message}`, {
    stack: err instanceof Error ? err.stack : undefined,
  });
}
